import React from 'react';
import { Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { IMedispanStagingDiff } from 'models/application-manager/medispan/IMedispanStagingDiff';
import { IMedispanStagingReview } from 'models/application-manager/medispan/IMedispanStagingReview';
import { MedispanStagingClient } from 'clients/medispan-staging-client';
import { UserUtils } from 'utils/user-utils';
import { UserPermissions } from 'interfaces/user/Permissions';
import { buildQaId } from 'utils/build-qa-id';
import { SectionTitleStyled } from '../components/section-title';
import { MedispanStagingTableStyled } from './medispan-staging-table';
import { RejectionReasonModalStyled } from './rejection-reason-modal';

const getQaId = buildQaId('application-manager.medispan-staging', '.');

const STATUS_LABELS: Record<IMedispanStagingReview['status'], string> = {
  no_data: 'No staged data available.',
  pending: 'Staged changes are awaiting review.',
  approved: 'Changes have been approved and are ready for execution.',
  rejected: 'Changes have been rejected.',
  executed: 'Changes have been successfully executed.',
};

const MedispanStaging = (): JSX.Element => {
  const dispatch = useDispatch();

  const [diff, setDiff] = React.useState<IMedispanStagingDiff[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [review, setReview] = React.useState<IMedispanStagingReview | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [actionLoading, setActionLoading] = React.useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = React.useState<boolean>(false);

  const canApprove = UserUtils.userIsPermitted(UserPermissions.MedispanStagingApprove);
  const canExecute = UserUtils.userIsPermitted(UserPermissions.MedispanStagingExecute);

  const loadData = async () => {
    setLoading(true);
    try {
      const [diffRes, statusRes] = await Promise.all([
        MedispanStagingClient.getStagingDiff(),
        MedispanStagingClient.getStatus(),
      ]);
      setDiff(diffRes.data.diffs);
      setTotal(diffRes.data.total);
      setReview(statusRes.data);
    } catch {
      dispatch(notifyError('Failed to load Medispan staging data.'));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await MedispanStagingClient.approve();
      dispatch(notifySuccess('Medispan staging changes approved.'));
      await loadData();
    } catch {
      dispatch(notifyError('Failed to approve Medispan staging changes.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    setShowRejectModal(false);
    setActionLoading(true);
    try {
      await MedispanStagingClient.reject(reason);
      dispatch(notifySuccess('Medispan staging changes rejected.'));
      await loadData();
    } catch {
      dispatch(notifyError('Failed to reject Medispan staging changes.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExecute = async () => {
    setActionLoading(true);
    try {
      await MedispanStagingClient.executeUpdate();
      dispatch(notifySuccess('Medispan update executed successfully.'));
      await loadData();
    } catch {
      dispatch(notifyError('Failed to execute Medispan update.'));
    } finally {
      setActionLoading(false);
    }
  };

  const renderActionBar = (): JSX.Element | null => {
    if (!review || review.status === 'no_data' || review.status === 'executed') {
      return null;
    }

    const isPending = review.status === 'pending';
    const isApproved = review.status === 'approved';

    return (
      <Grid container spacing={1} style={{ marginBottom: 16 }}>
        {canApprove && isPending && (
          <>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                disabled={actionLoading}
                onClick={handleApprove}
                data-qa-id={getQaId('approve-button')}
              >
                Approve
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="error"
                disabled={actionLoading}
                onClick={() => setShowRejectModal(true)}
                data-qa-id={getQaId('reject-button')}
              >
                Reject
              </Button>
            </Grid>
          </>
        )}
        {canExecute && isApproved && (
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              disabled={actionLoading}
              onClick={handleExecute}
              data-qa-id={getQaId('execute-button')}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Execute Update'}
            </Button>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <>
      <SectionTitleStyled title="Medispan Staging Report" />
      {loading ? (
        <Grid container justifyContent="center" style={{ padding: 32 }}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {review && (
            <Typography
              variant="body2"
              style={{ marginBottom: 12 }}
              data-qa-id={getQaId('status-label')}
            >
              {STATUS_LABELS[review.status]}
              {review.status === 'rejected' && review.rejection_reason && (
                <>
                  {' '}
                  Reason: <em>{review.rejection_reason}</em>
                </>
              )}
            </Typography>
          )}
          {total > 0 && (
            <Typography
              variant="body2"
              style={{ marginBottom: 12 }}
              data-qa-id={getQaId('summary')}
            >
              Total modified records: {total}
            </Typography>
          )}
          {renderActionBar()}
          {diff.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ padding: 32, textAlign: 'center' }}
            >
              No staging differences found.
            </Typography>
          ) : (
            <MedispanStagingTableStyled rows={diff} />
          )}
        </>
      )}
      <RejectionReasonModalStyled
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onSubmit={handleReject}
      />
    </>
  );
};

export default MedispanStaging;
